import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/Widgets";
import { useAuth } from "@/context/AuthContext";
import { errMsg, http } from "@/lib/api";

const ROLES = { admin: "Administrator", modeller: "Financial modeller", viewer: "Leadership viewer (read-only)" };

const RoleSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-[220px] border-slate-700 bg-slate-900/70 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-[#131c2e]">{Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}</SelectContent>
  </Select>
);

const NewUser = ({ onDone }) => {
  const [f, setF] = useState({ email: "", name: "", password: "", role: "viewer" });
  const save = async () => {
    try {
      await http.post("/auth/users", f);
      toast.success("User created");
      setF({ email: "", name: "", password: "", role: "viewer" });
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" ") : errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="new-user-form">
      {["email", "name", "password"].map((k) => (
        <Input key={k} type={k === "password" ? "password" : "text"} placeholder={k === "password" ? "Temporary password (8+ chars)" : k[0].toUpperCase() + k.slice(1)} value={f[k]}
          onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-8 w-52 border-slate-700 bg-slate-900/70 text-xs" data-testid={`new-user-${k}-input`} />
      ))}
      <RoleSelect value={f.role} onChange={(v) => setF({ ...f, role: v })} testid="new-user-role-select" />
      <Button size="sm" onClick={save} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="new-user-submit-button">Create user</Button>
    </div>
  );
};

export function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const load = useCallback(() => http.get("/auth/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="users-page">
      <PageHeader kicker="Administration" title="Users & Roles" />
      <Panel title="Create account" subtitle="No public sign-up: only administrators can add users"><NewUser onDone={load} /></Panel>
      <Panel className="mt-6" title="Accounts" testid="users-table">
        <table className="w-full text-sm">
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-800" data-testid={`user-row-${u.email}`}>
                <td className="py-2.5 text-slate-100">{u.email}<div className="text-[11px] text-slate-500">{u.name}</div></td>
                <td className="py-2.5"><RoleSelect value={u.role} onChange={(v) => act(() => http.patch(`/auth/users/${u.id}`, { role: v }), "Role updated")} testid={`user-role-${u.email}`} /></td>
                <td className="py-2.5 text-right">
                  {u.id !== user.id && <Button size="sm" variant="ghost" className="text-rose-400" data-testid={`user-delete-${u.email}`} onClick={() => act(() => http.delete(`/auth/users/${u.id}`), "User removed")}>Remove</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

export function AuditPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { http.get("/audit").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  return (
    <div data-testid="audit-page">
      <PageHeader kicker="Governance" title="Audit Log" />
      <Panel title="Uploads, confirmations, version and mapping changes, sign-ins" testid="audit-table">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-800">
                  <td className="num py-2 pr-3 text-xs text-slate-500">{new Date(r.ts).toLocaleString()}</td>
                  <td className="py-2 pr-3 text-xs text-slate-300">{r.user || "system"}</td>
                  <td className="py-2 pr-3 text-xs text-amber-400">{r.action.replace(/_/g, " ")}</td>
                  <td className="py-2 text-xs text-slate-400">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
